import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BrainCircuit,
  ShieldCheck,
  Sparkles,
  Timer,
  TrendingUp,
  BarChart3,
  BriefcaseBusiness,
  ChevronLeft,
  ChevronRight,
  Workflow,
  BadgeCheck,
  DollarSign,
  Search,
  LayoutDashboard,
  ClipboardList,
  Rocket,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

const PROCESS_LINE_COLOR = '#C9D0D9';
const PROCESS_ACTIVE_COLOR = '#FF5722';

const ConsultingProcessMap = ({ steps, isActive, accentClass }) => {
  const [hasEntered, setHasEntered] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(-1);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setActiveStepIndex(-1);
      setIsComplete(false);
      return undefined;
    }

    if (!hasEntered) {
      return undefined;
    }

    setActiveStepIndex(-1);
    setIsComplete(false);

    const timers = [
      window.setTimeout(() => setActiveStepIndex(0), 650),
      window.setTimeout(() => setActiveStepIndex(1), 1450),
      window.setTimeout(() => setActiveStepIndex(2), 2250),
      window.setTimeout(() => setActiveStepIndex(3), 3050),
      window.setTimeout(() => setIsComplete(true), 3600),
    ];

    return () => timers.forEach((timerId) => window.clearTimeout(timerId));
  }, [hasEntered, isActive]);

  return (
    <motion.div
      className="terminal-grid p-6"
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: isActive ? 1 : 0.3, x: isActive ? 0 : 24 }}
      transition={{ duration: 0.65, ease: 'easeOut' }}
      onViewportEnter={() => setHasEntered(true)}
      viewport={{ amount: 0.45 }}
      style={{
        borderColor: isComplete ? 'rgba(255, 87, 34, 0.18)' : undefined,
        boxShadow: isComplete ? '0 0 36px rgba(255, 87, 34, 0.12)' : undefined,
      }}
    >
      <div className={`inline-flex items-center gap-2 mb-5 text-xs uppercase tracking-[0.18em] font-mono ${accentClass}`}>
        <Workflow size={14} /> Consulting process map
      </div>

      <div className="relative">
        <svg
          className="absolute left-[19px] top-4 h-[calc(100%-2rem)] w-6 overflow-visible pointer-events-none"
          viewBox="0 0 24 360"
          fill="none"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <motion.path
            d="M12 0 C12 42 12 42 12 84 C12 126 12 126 12 168 C12 210 12 210 12 252 C12 294 12 294 12 336"
            stroke={PROCESS_LINE_COLOR}
            strokeWidth="1.8"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0.35 }}
            animate={{ pathLength: isActive && hasEntered ? 1 : 0, opacity: isActive && hasEntered ? 0.95 : 0.2 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </svg>

        <div className="space-y-4">
          {steps.map((step, index) => {
            const isCurrent = activeStepIndex === index;
            const isLit = activeStepIndex >= index;
            const contentVisible = activeStepIndex >= index;
            const shouldGlow = isCurrent || (isComplete && index === steps.length - 1);

            return (
              <div key={step.step} className="relative grid grid-cols-[44px_minmax(0,1fr)] gap-4 items-start">
                <div className="relative flex justify-center pt-1">
                  <motion.div
                    className="relative z-10 w-10 h-10 rounded-full border flex items-center justify-center bg-transparent"
                    animate={{
                      borderColor: shouldGlow ? PROCESS_ACTIVE_COLOR : (isComplete ? 'rgba(201,208,217,0.72)' : 'rgba(201,208,217,0.52)'),
                      boxShadow: shouldGlow
                        ? [
                            '0 0 0 rgba(255,87,34,0)',
                            '0 0 0 6px rgba(255,87,34,0.16)',
                            '0 0 24px rgba(255,87,34,0.58)',
                          ]
                        : isComplete
                          ? '0 0 12px rgba(255,255,255,0.08)'
                          : '0 0 0 rgba(0,0,0,0)',
                      backgroundColor: shouldGlow ? 'rgba(255,87,34,0.08)' : 'rgba(8,12,22,0.2)',
                      scale: isCurrent ? [1, 1.08, 1] : 1,
                    }}
                    transition={{
                      duration: index === steps.length - 1 && shouldGlow ? 0.24 : 0.5,
                      repeat: shouldGlow ? (index === steps.length - 1 ? Infinity : 1) : 0,
                      repeatType: 'reverse',
                    }}
                  >
                    <step.icon size={16} color={shouldGlow ? PROCESS_ACTIVE_COLOR : isLit ? '#F3F4F6' : PROCESS_LINE_COLOR} />
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, x: -24 }}
                  animate={{
                    opacity: contentVisible && isActive ? 1 : 0.32,
                    x: contentVisible && isActive ? 0 : -24,
                    borderColor: shouldGlow
                      ? 'rgba(255, 87, 34, 0.4)'
                      : isComplete
                        ? 'rgba(201,208,217,0.22)'
                        : 'rgba(255,255,255,0.08)',
                    boxShadow: isComplete
                      ? '0 0 14px rgba(255,255,255,0.05)'
                      : shouldGlow
                        ? '0 0 22px rgba(255,87,34,0.12)'
                        : '0 0 0 rgba(0,0,0,0)',
                  }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  className="rounded-xl border p-4 bg-[rgba(7,12,22,0.72)] backdrop-blur-sm"
                >
                  <p className="text-[10px] font-mono tracking-[0.16em]" style={{ color: shouldGlow ? PROCESS_ACTIVE_COLOR : '#D0D5DD' }}>
                    STEP {step.step}
                  </p>
                  <p className="text-white font-semibold mt-1">{step.title}</p>
                  <p className="text-xs text-text-secondary mt-1">{step.detail}</p>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

const ConfidenceRiskChart = ({ isActive, accentClass }) => {
  const rows = [
    { name: 'ChatGPT Plus', confidence: 94, risk: 18, confidenceLabel: '4.9', riskLabel: 'Low' },
    { name: 'Claude Pro', confidence: 91, risk: 23, confidenceLabel: '4.8', riskLabel: 'Low' },
    { name: 'Gemini Advanced', confidence: 84, risk: 33, confidenceLabel: '4.5', riskLabel: 'Medium' },
  ];

  const riskTone = (risk) => {
    if (risk >= 35) {
      return {
        badge: 'border-amber-300/40 bg-amber-500/10 text-amber-200',
        bar: 'from-amber-300 to-orange-300',
      };
    }
    return {
      badge: 'border-laser-green/40 bg-laser-green/10 text-laser-green',
      bar: 'from-laser-green to-cyan-300',
    };
  };

  return (
    <div className="terminal-grid p-6">
      <div className={`inline-flex items-center gap-2 mb-4 text-xs uppercase tracking-[0.18em] font-mono ${accentClass}`}>
        <BriefcaseBusiness size={14} /> Confidence and risk map
      </div>

      <div className="grid grid-cols-[1.2fr_0.95fr] gap-2 mb-2 text-[10px] uppercase tracking-[0.16em] font-mono text-text-muted">
        <span>Tool</span>
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <span>Confidence</span>
          <span>Risk</span>
        </div>
      </div>

      <div className="space-y-3">
        {rows.map((row, index) => {
          const tone = riskTone(row.risk);
          return (
            <motion.div
              key={row.name}
              className="rounded-lg border border-white/10 p-3 bg-black/35"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: isActive ? 1 : 0.45, y: isActive ? 0 : 10 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <div className="grid grid-cols-[1.2fr_0.95fr] gap-2 items-center">
                <div>
                  <p className="text-sm text-white font-semibold">{row.name}</p>
                  <p className="text-[11px] text-text-muted mt-0.5">Fit score {row.confidenceLabel}</p>
                </div>

                <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                  <div>
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-cyan-300 to-blue-300"
                        initial={{ width: 0 }}
                        animate={{ width: isActive ? `${row.confidence}%` : 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut', delay: index * 0.1 + 0.15 }}
                      />
                    </div>
                    <div className="mt-1 text-[10px] font-mono text-cyan-200">{row.confidence}% confidence</div>
                  </div>

                  <div className="w-[84px]">
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                      <motion.div
                        className={`h-full rounded-full bg-gradient-to-r ${tone.bar}`}
                        initial={{ width: 0 }}
                        animate={{ width: isActive ? `${row.risk}%` : 0 }}
                        transition={{ duration: 0.65, ease: 'easeOut', delay: index * 0.1 + 0.22 }}
                      />
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${tone.badge}`}>{row.riskLabel}</span>
                      <span className="text-[10px] font-mono text-white/70">{row.risk}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        className="mt-4 rounded-lg border border-cyan-300/20 bg-cyan-500/10 p-3"
        animate={isActive ? { boxShadow: ['0 0 0 rgba(34,211,238,0)', '0 0 16px rgba(34,211,238,0.22)', '0 0 0 rgba(34,211,238,0)'] } : {}}
        transition={{ duration: 1.8, repeat: Infinity }}
      >
        <p className="text-xs text-cyan-100 font-semibold">Decision signal</p>
        <p className="text-xs text-text-secondary mt-1">
          High confidence and lower execution risk indicate a safer first-buy path for most operator teams.
        </p>
      </motion.div>
    </div>
  );
};

const HeroPrimed = () => {
  const [counter, setCounter] = useState(3128);
  const [activeScreen, setActiveScreen] = useState(0);
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const screens = useMemo(
    () => [
      {
        id: 'confidence',
        theme: {
          kicker: 'text-cyan-200',
          accent: 'text-cyan-300',
          chipRing: 'border-cyan-300/35',
          chipBg: 'bg-cyan-500/10',
          button: 'bg-cyan-400 text-slate-900 hover:bg-cyan-300',
          link: 'text-cyan-200 hover:text-cyan-100',
          pulse: 'bg-cyan-400',
          signal: 'text-cyan-200',
          sideHeader: 'text-cyan-200',
          quickLink: 'border-cyan-300/35 hover:border-cyan-200 hover:text-cyan-200',
          orbA: 'from-cyan-400/35 to-blue-500/15',
          orbB: 'from-yellow-300/30 to-cyan-300/10',
        },
        outcomes: [
          { icon: DollarSign, value: '$18K avg saved', label: 'per wrong tool avoided' },
          { icon: Timer, value: '3x faster', label: 'stack evaluation cycle' },
          { icon: TrendingUp, value: '94%', label: 'decision confidence score' },
        ],
        kicker: 'Trusted AI buying advisor for operators',
        title: 'Stop second-guessing your stack.',
        highlight: 'Buy AI tools with confidence.',
        subtitle:
          'We start where most teams feel stuck: tool overwhelm, hidden tradeoffs, and fear of choosing wrong. Wowrolla guides you from uncertainty to a clean, defendable decision.',
        bgImage: isLight
          ? "linear-gradient(180deg, rgba(255,248,232,0.56) 0%, rgba(247,251,255,0.68) 54%, rgba(242,248,255,0.78) 100%), linear-gradient(115deg, rgba(255,236,186,0.30) 0%, rgba(198,235,255,0.20) 44%, rgba(231,243,255,0.48) 100%), url('/images/robotRaisedhand.jpg')"
          : "linear-gradient(180deg, rgba(7,18,30,0.62) 0%, rgba(7,16,28,0.68) 56%, rgba(7,14,26,0.72) 100%), linear-gradient(115deg, rgba(17,50,73,0.52) 0%, rgba(6,19,31,0.34) 42%, rgba(6,19,31,0.66) 100%), url('/images/robotRaisedhand.jpg')",
        chips: [
          { icon: BrainCircuit, label: 'Bias-resistant recommendations', color: 'text-cyber-yellow' },
          { icon: Sparkles, label: 'Implementation-first scoring', color: 'text-laser-green' },
        ],
        statCards: [
          { icon: ShieldCheck, text: 'Commission transparency on every major page', color: 'text-cyber-yellow' },
          { icon: Timer, text: 'Avg. evaluation cycle cut from weeks to days', color: 'text-laser-green' },
          { icon: TrendingUp, text: `${counter.toLocaleString()} guided stack decisions`, color: 'text-cyber-yellow' },
        ],
        quickLinks: [
          { to: '/tools/chatgpt', label: 'Explore ChatGPT' },
          { to: '/tools/claude', label: 'Explore Claude' },
          { to: '/compare', label: 'Compare top assistants' },
        ],
      },
      {
        id: 'consulting-process',
        theme: {
          kicker: 'text-amber-200',
          accent: 'text-orange-300',
          chipRing: 'border-orange-300/35',
          chipBg: 'bg-orange-500/10',
          button: 'bg-orange-400 text-slate-900 hover:bg-orange-300',
          link: 'text-orange-200 hover:text-orange-100',
          pulse: 'bg-orange-300',
          signal: 'text-orange-200',
          sideHeader: 'text-orange-200',
          quickLink: 'border-orange-300/35 hover:border-orange-200 hover:text-orange-200',
          orbA: 'from-orange-400/35 to-amber-500/15',
          orbB: 'from-rose-300/25 to-orange-300/10',
        },
        outcomes: [
          { icon: DollarSign, value: 'ROI in 90 days', label: 'or gate extension triggered' },
          { icon: TrendingUp, value: '2.8x avg lift', label: 'on your target KPI' },
          { icon: BadgeCheck, value: 'Pilot-first', label: 'no lock-in until proven' },
        ],
        kicker: 'Consulting execution model',
        title: 'From Discovery to Deployment.',
        highlight: 'A process teams can trust.',
        subtitle:
          'Our consulting lane is built for speed and clarity: define workflow, score readiness, prescribe stack, and launch with measurable ROI checkpoints.',
        incentive: {
          headline: 'Outcome-based incentive: we scale only when your KPI lift is measurable.',
          detail:
            'Zero-risk strategy: launch with a scoped pilot, success gates, and explicit before/after metrics. If outcomes are not met in scope, you are not pushed into long-term lock-in.',
        },
        bgImage: isLight
          ? "linear-gradient(180deg, rgba(255,245,224,0.54) 0%, rgba(252,248,238,0.66) 54%, rgba(246,250,255,0.78) 100%), linear-gradient(110deg, rgba(255,216,156,0.26) 0%, rgba(171,215,255,0.20) 56%, rgba(229,241,255,0.46) 100%), url('/images/handtohand.jpg')"
          : "linear-gradient(180deg, rgba(18,12,8,0.58) 0%, rgba(13,18,29,0.66) 58%, rgba(9,12,22,0.72) 100%), linear-gradient(110deg, rgba(141,98,40,0.30) 0%, rgba(20,34,55,0.42) 55%, rgba(7,14,24,0.70) 100%), url('/images/handtohand.jpg')",
        process: [
          { step: '01', title: 'Discovery', detail: 'Map the workflow bottleneck and target business outcome.', icon: Search },
          { step: '02', title: 'Readiness', detail: 'Audit data, systems, and adoption risk before tool selection.', icon: LayoutDashboard },
          { step: '03', title: 'Prescription', detail: 'Deliver a lean stack with package and ROI model.', icon: ClipboardList },
          { step: '04', title: 'Launch', detail: 'Deploy, train teams, and monitor before/after metrics.', icon: Rocket },
        ],
        quickLinks: [
          { to: '/consulting#start', label: 'Book discovery call' },
          { to: '/consulting#roi', label: 'Run readiness studio' },
          { to: '/consulting#packages', label: 'See packages' },
        ],
      },
      {
        id: 'before-after',
        theme: {
          kicker: 'text-emerald-200',
          accent: 'text-emerald-300',
          chipRing: 'border-emerald-300/35',
          chipBg: 'bg-emerald-500/10',
          button: 'bg-emerald-400 text-slate-900 hover:bg-emerald-300',
          link: 'text-emerald-200 hover:text-emerald-100',
          pulse: 'bg-emerald-300',
          signal: 'text-emerald-200',
          sideHeader: 'text-emerald-200',
          quickLink: 'border-emerald-300/35 hover:border-emerald-200 hover:text-emerald-200',
          orbA: 'from-emerald-400/30 to-teal-500/15',
          orbB: 'from-sky-300/25 to-emerald-300/10',
        },
        outcomes: [
          { icon: DollarSign, value: '$31K/yr', label: 'recovered from manual ops' },
          { icon: Timer, value: '-65%', label: 'lead response latency' },
          { icon: TrendingUp, value: '4x ROI', label: 'documented at 6 months' },
        ],
        kicker: 'Operational impact snapshot',
        title: 'Before vs After AI Implementation.',
        highlight: 'Show the delta, not the hype.',
        subtitle:
          'Winning implementations are measured. We track cycle time, quality, and throughput so teams can see what changed and why.',
        bgImage: isLight
          ? "linear-gradient(180deg, rgba(240,247,255,0.54) 0%, rgba(248,252,255,0.68) 54%, rgba(244,249,255,0.78) 100%), linear-gradient(110deg, rgba(155,226,255,0.24) 0%, rgba(171,238,210,0.18) 45%, rgba(224,243,255,0.44) 100%), url('/images/compare.jpg')"
          : "linear-gradient(180deg, rgba(9,15,28,0.62) 0%, rgba(8,17,32,0.66) 56%, rgba(9,12,23,0.74) 100%), linear-gradient(110deg, rgba(23,92,112,0.34) 0%, rgba(18,34,56,0.34) 45%, rgba(6,12,22,0.66) 100%), url('/images/compare.jpg')",
        beforeAfter: [
          {
            metric: 'Lead response latency',
            plainLabel: 'Reply speed',
            explainer: 'Customers get answers within hours, not next day.',
            beforeLabel: 'Slow response',
            afterLabel: 'Fast response',
            gain: '87% faster',
            progress: 87,
            icon: Timer,
            before: '19h',
            after: '2.4h',
          },
          {
            metric: 'Manual admin per week',
            plainLabel: 'Team busywork',
            explainer: 'Admin time is redirected into client and revenue work.',
            beforeLabel: 'Busywork load',
            afterLabel: 'Freed capacity',
            gain: '69% less admin',
            progress: 69,
            icon: BriefcaseBusiness,
            before: '26h',
            after: '8h',
          },
          {
            metric: 'Cross-team handoff errors',
            plainLabel: 'Dropped tasks',
            explainer: 'Fewer handoff misses means cleaner delivery and trust.',
            beforeLabel: 'Frequent misses',
            afterLabel: 'Reliable handoffs',
            gain: '76% fewer errors',
            progress: 76,
            icon: Workflow,
            before: '17%',
            after: '4%',
          },
        ],
        quickLinks: [
          { to: '/tools/n8n', label: 'Automation stack: n8n' },
          { to: '/ai-stacks', label: 'View proven AI stacks' },
          { to: '/find', label: 'Find your workflow fit' },
        ],
      },
    ],
    [counter, isLight]
  );

  useEffect(() => {
    const id = window.setInterval(() => {
      setActiveScreen((prev) => (prev + 1) % screens.length);
    }, 20000);

    return () => window.clearInterval(id);
  }, [screens.length]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setCounter((prev) => prev + Math.floor(Math.random() * 3));
    }, 3200);
    return () => window.clearInterval(id);
  }, []);

  return (
    <section className="relative min-h-[calc(100vh-72px)] lg:min-h-[calc(100vh-80px)] flex items-center overflow-hidden mt-0 mb-0 home-hero-banner" data-testid="hero-primed">
      <div className="absolute inset-0 prime-aurora-bg" />

      <div className="absolute top-4 right-4 z-20 hidden md:flex items-center gap-2">
        <button
          type="button"
          onClick={() => setActiveScreen((prev) => (prev === 0 ? screens.length - 1 : prev - 1))}
          className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-white/20 bg-black/45 text-white hover:border-cyber-yellow/60 transition-colors"
          aria-label="Previous hero screen"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => setActiveScreen((prev) => (prev + 1) % screens.length)}
          className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-white/20 bg-black/45 text-white hover:border-cyber-yellow/60 transition-colors"
          aria-label="Next hero screen"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <motion.div
        className="flex w-full relative z-10"
        animate={{ x: `-${activeScreen * 100}%` }}
        transition={{ duration: 0.85, ease: 'easeInOut' }}
      >
        {screens.map((item, index) => {
          const isActive = index === activeScreen;

          return (
          <div key={item.id} className="w-full flex-shrink-0 relative">
            <motion.div
              className="absolute inset-0 hero-primed-bg home-parallax-bg"
              style={{
                backgroundImage: item.bgImage,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              animate={{ scale: isActive ? 1.03 : 1 }}
              transition={{ duration: 4.5, ease: 'easeOut' }}
            />

            <div className="container mx-auto px-6 py-[clamp(1.5rem,5vh,4rem)] relative z-10 min-h-[calc(100vh-72px)] lg:min-h-[calc(100vh-80px)] flex flex-col justify-center">
              <motion.div
                className={`absolute -top-20 right-[8%] w-64 h-64 rounded-full blur-3xl bg-gradient-to-br ${item.theme.orbA}`}
                animate={{ y: isActive ? [0, -12, 0] : 0, scale: isActive ? [1, 1.08, 1] : 1 }}
                transition={{ duration: 7.5, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className={`absolute bottom-14 left-[4%] w-56 h-56 rounded-full blur-3xl bg-gradient-to-br ${item.theme.orbB}`}
                animate={{ y: isActive ? [0, 10, 0] : 0, scale: isActive ? [1, 0.95, 1] : 1 }}
                transition={{ duration: 8.4, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
              />

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                  <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: isActive ? 1 : 0.25, y: isActive ? 0 : 8 }}
                    className={`text-xs tracking-[0.2em] uppercase font-mono mb-5 ${item.theme.kicker}`}
                  >
                    {item.kicker}
                  </motion.p>

                  <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: isActive ? 1 : 0.35, y: isActive ? 0 : 8 }}
                    transition={{ duration: 0.55, ease: 'easeOut' }}
                    className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight max-w-5xl"
                    style={{ fontFamily: 'Unbounded, sans-serif' }}
                  >
                    {item.title}
                    <span className={item.theme.accent}> {item.highlight}</span>
                  </motion.h1>

                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: isActive ? 180 : 0, opacity: isActive ? 1 : 0 }}
                    transition={{ duration: 0.65 }}
                    className={`h-[3px] mt-5 rounded-full ${item.theme.pulse}`}
                  />

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isActive ? 1 : 0.3 }}
                    transition={{ duration: 0.5 }}
                    className="mt-6 text-lg text-text-secondary max-w-3xl"
                  >
                    {item.subtitle}
                  </motion.p>

                  {item.outcomes && (
                    <motion.div
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: isActive ? 1 : 0.25, y: isActive ? 0 : 14 }}
                      transition={{ duration: 0.55, delay: 0.15 }}
                      className="mt-6 flex flex-wrap gap-3"
                    >
                      {item.outcomes.map((outcome, oi) => (
                        <motion.div
                          key={outcome.label}
                          className={`inline-flex items-center gap-2 px-4 py-2 bg-black/60 border ${item.theme.chipRing} rounded-full backdrop-blur-sm`}
                          animate={isActive ? { y: [0, -5, 0] } : {}}
                          transition={{ duration: 2.8, repeat: Infinity, delay: oi * 0.45, ease: 'easeInOut' }}
                          whileHover={{ scale: 1.06 }}
                        >
                          <outcome.icon size={13} className={item.theme.accent} />
                          <span className={`text-sm font-bold ${item.theme.accent}`}>{outcome.value}</span>
                          <span className="text-xs text-white/65">{outcome.label}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}

                  {item.chips && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {item.chips.map((chip) => (
                        <motion.span
                          key={chip.label}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-black/55 border text-white ${item.theme.chipRing}`}
                          whileHover={{ y: -2, scale: 1.02 }}
                        >
                          <chip.icon size={13} className={chip.color} />
                          {chip.label}
                        </motion.span>
                      ))}
                    </div>
                  )}

                  {item.incentive && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: isActive ? 1 : 0.35, y: isActive ? 0 : 10 }}
                      transition={{ duration: 0.6 }}
                      className={`mt-6 p-4 border ${item.theme.chipRing} ${item.theme.chipBg}`}
                    >
                      <p className="text-sm md:text-base text-white font-semibold inline-flex items-center gap-2">
                        <BadgeCheck size={16} className={item.theme.accent} />
                        {item.incentive.headline}
                      </p>
                      <p className="text-sm mt-2 text-text-secondary">{item.incentive.detail}</p>
                    </motion.div>
                  )}

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: isActive ? 1 : 0.35, y: isActive ? 0 : 8 }}
                    transition={{ duration: 0.5 }}
                    className="mt-8 flex flex-col gap-3"
                  >
                    <div>
                      <Link to="/find" className={`inline-flex items-center gap-2 px-5 py-3 font-semibold transition-all ${item.theme.button}`}>
                        Find my perfect AI stack
                        <ArrowRight size={18} />
                      </Link>
                    </div>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      Need a full stack audit?{' '}
                      <Link
                        to="/consulting#start"
                        className={`underline underline-offset-2 transition-colors ${item.theme.link}`}
                      >
                        Book a consulting session
                      </Link>
                    </p>
                  </motion.div>

                  {item.quickLinks && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: isActive ? 1 : 0.25, y: isActive ? 0 : 8 }}
                      transition={{ duration: 0.55 }}
                      className="mt-4 flex flex-wrap gap-2"
                    >
                      {item.quickLinks.map((quickLink) => (
                        <Link
                          key={quickLink.to}
                          to={quickLink.to}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs border bg-black/45 text-white transition-colors ${item.theme.quickLink}`}
                        >
                          {quickLink.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}

                  {item.statCards && (
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
                      {item.statCards.map((card) => (
                        <motion.div
                          key={card.text}
                          className="prime-glow-card p-3 flex items-center gap-2 transition-all"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: isActive ? 1 : 0.35, y: isActive ? 0 : 8 }}
                          transition={{ duration: 0.45 }}
                        >
                          <card.icon size={16} className={card.color} />
                          <span className="text-sm text-text-secondary">{card.text}</span>
                        </motion.div>
                      ))}
                    </div>
                  )}

                </div>

                <div className="hidden lg:block lg:col-span-5">
                  {item.process && (
                    <ConsultingProcessMap
                      steps={item.process}
                      isActive={isActive}
                      accentClass={item.theme.sideHeader}
                    />
                  )}

                  {item.beforeAfter && (
                    <div className="terminal-grid p-6">
                      <div className={`inline-flex items-center gap-2 mb-4 text-xs uppercase tracking-[0.18em] font-mono ${item.theme.sideHeader}`}>
                        <BarChart3 size={14} /> Before / After impact
                      </div>
                      <div className="space-y-3">
                        {item.beforeAfter.map((row, rowIndex) => (
                          <motion.div
                            key={row.metric}
                            className="rounded-lg border border-white/10 p-3 bg-black/30"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: isActive ? 1 : 0.45, y: isActive ? 0 : 10 }}
                            transition={{ duration: 0.4, delay: rowIndex * 0.12 }}
                          >
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex items-start gap-2">
                                <motion.div
                                  className="w-8 h-8 rounded-full border border-white/15 bg-white/5 inline-flex items-center justify-center mt-0.5"
                                  animate={isActive ? { scale: [1, 1.06, 1] } : { scale: 1 }}
                                  transition={{ duration: 1.8, repeat: Infinity, delay: rowIndex * 0.2 }}
                                >
                                  <row.icon size={14} className="text-emerald-300" />
                                </motion.div>
                                <div>
                                  <p className="text-sm text-white font-semibold">{row.plainLabel}</p>
                                  <p className="text-[11px] text-text-muted">{row.metric}</p>
                                </div>
                              </div>
                              <motion.span
                                className="text-[11px] px-2 py-1 rounded border border-emerald-300/35 bg-emerald-500/10 text-emerald-200 font-semibold"
                                animate={isActive ? { boxShadow: ['0 0 0 rgba(16,185,129,0)', '0 0 12px rgba(16,185,129,0.3)', '0 0 0 rgba(16,185,129,0)'] } : {}}
                                transition={{ duration: 1.6, repeat: Infinity }}
                              >
                                {row.gain}
                              </motion.span>
                            </div>

                            <p className="text-xs text-text-secondary mb-3">{row.explainer}</p>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="border border-red-400/20 bg-red-500/10 rounded p-2">
                                <p className="text-[10px] uppercase tracking-[0.14em] text-red-300 font-mono">Before · {row.beforeLabel}</p>
                                <p className="text-white text-lg font-bold">{row.before}</p>
                              </div>
                              <div className="border border-laser-green/20 bg-laser-green/10 rounded p-2">
                                <p className="text-[10px] uppercase tracking-[0.14em] text-laser-green font-mono">After · {row.afterLabel}</p>
                                <p className="text-white text-lg font-bold">{row.after}</p>
                              </div>
                            </div>

                            <div className="mt-3">
                              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] font-mono text-text-muted mb-1">
                                <span>Impact progress</span>
                                <span>{row.progress}%</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                                <motion.div
                                  className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-cyan-300"
                                  initial={{ width: 0 }}
                                  animate={{ width: isActive ? `${row.progress}%` : 0 }}
                                  transition={{ duration: 0.65, ease: 'easeOut', delay: rowIndex * 0.12 + 0.15 }}
                                />
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}

                  {item.id === 'confidence' && (
                    <ConfidenceRiskChart isActive={isActive} accentClass={item.theme.sideHeader} />
                  )}
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </motion.div>

      <div className="absolute bottom-3 left-0 right-0 z-20 flex items-center justify-center gap-2">
        {screens.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setActiveScreen(index)}
            className={`h-2 rounded-full transition-all ${activeScreen === index ? `w-10 ${item.theme.pulse}` : 'w-4 bg-white/45 hover:bg-white/70'}`}
            aria-label={`Go to ${item.id} screen`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroPrimed;

